import {
  add,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  formatDuration,
  Interval,
  intervalToDuration,
  parse,
  startOfDay,
  sub,
} from 'date-fns'

import { UTCDate } from '@date-fns/utc'

/**
 * Helper functions for date(s)
 */
import { UserLanguage } from '../features/account/types/User'
import { GranularityValue } from '../features/revenue-and-downloads/types/Filters'
import languageService from '../services/LanguageService'

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24

const userLanguageToLocaleMap = {
  en: 'en-US',
  ja: 'ja-JP',
  zh: 'zh-CN',
}

const defaultDateFormat: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

/**
 * Decides if the given date is an actual Date
 */
export const isValidDate = (d: any) => {
  return d instanceof Date && !isNaN(d.getTime()) && d.getFullYear() >= 1000
}

/**
 * Creates a date time formatter with given language and DateTimeFormatOptions
 */
export const getDateTimeFormatter = (language: UserLanguage, options: Intl.DateTimeFormatOptions = defaultDateFormat) => {
  const locale = userLanguageToLocaleMap[language] || userLanguageToLocaleMap[languageService.defaultLanguage]
  return new Intl.DateTimeFormat(locale, options)
}

type DateTimeFormatterOptions = Intl.DateTimeFormatOptions & {
  language: UserLanguage
}

/**
 * Formats a date with given options
 */
export const formatDate = (date: number | Date, options: DateTimeFormatterOptions) => {
  const { language, ...otherOptions } = options
  const resolvedOptions = Object.keys(otherOptions).length > 0 ? otherOptions : undefined
  const formatter = getDateTimeFormatter(options.language, resolvedOptions)

  return formatter.format(date)
}

/**
 * Get difference in months between two dates
 */
export const monthDifference = (dateFrom: Date, dateTo: Date) => {
  return dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear())
}

/**
 * Get difference in days between two dates
 */
export const dateDifference = (pastDate: Date, futureDate: Date) => {
  const utc1 = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate())
  const utc2 = Date.UTC(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate())

  return Math.floor((utc2 - utc1) / MILLISECONDS_PER_DAY)
}

/**
 * Resolves to the start of the next day
 */
export const startOfNextDay = (ts: number | Date) => {
  return startOfDay(add(ts, { days: 1 })).getTime()
}

/**
 * Resolves to the start of the next day
 */
export const startOfPreviousDay = (ts: number | Date) => {
  return startOfDay(sub(ts, { days: 1 })).getTime()
}

/**
 * Resolves to the end of the previous monts
 */
export const endOfPreviousMonth = (ts: number | Date) => {
  return endOfMonth(sub(ts, { months: 1 })).getTime()
}

/**
 * Resolves to the end of the previous day
 */
export const endOfPreviousDay = (ts: number | Date) => {
  return endOfDay(sub(ts, { days: 1 })).getTime()
}

/**
 * Checks if give Interval (date-fns) is valid
 */
export const isValidInterval = (interval: Interval) => {
  return !!interval.start && !!interval.end && interval.start <= interval.end //&& +interval.start - +interval.end !== 0
}

/**
 * Formats a calendar-day duration anchored at January 1 of the given year.
 *
 * Anchoring at Jan 1 honours leap-year February (29 days) when the anchor
 * year is a leap year and non-leap February (28 days) otherwise, while
 * keeping the rendered output independent of which month the underlying
 * event actually starts in. That matches QA's expected behaviour for the
 * GR-1841 examples:
 *  - 59 days anchored at Jan 1 2024 (leap)  → "1 month 28 days"
 *  - 58 days anchored at Jan 1 2026 (non-leap) → "1 month 27 days"
 *
 * `add(anchorStart, { days })` walks calendar days, so the helper is
 * DST-safe even when the resulting end lands after a spring-forward.
 */
export const formatDurationDays = (
  days: number,
  anchorYear: number,
  formatParts: Array<'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds'> = ['years', 'months', 'days']
) => {
  const anchorStart = new Date(anchorYear, 0, 1)
  const anchorEnd = add(anchorStart, { days })
  return formatDuration(intervalToDuration({ start: anchorStart, end: anchorEnd }), { format: formatParts })
}

/**
 * Formats the calendar duration of a live event instance.
 *
 * Computes the calendar-day length of `[start, end]` (inclusive on both
 * ends, via `startOfNextDay`) and renders it through `formatDurationDays`
 * anchored at Jan 1 of the event's start year.
 *
 * History: an earlier revision built a synthetic `{ start: 0, end: ms }`
 * interval and fed it to `intervalToDuration`, which silently anchored at
 * the Unix epoch (Jan 1 1970, non-leap). That mis-rendered 59 leap-year
 * days as a flat "2 months". A follow-up anchored at the event's actual
 * start, which then mis-rendered 58 non-leap-year days starting in
 * February as "1 month 30 days" (because Feb has 28 days). Anchoring at
 * Jan 1 of the start year matches QA's expectations for both cases —
 * see GR-1841.
 */
export const formatEventInstanceDuration = (
  start: number | Date,
  end: number | Date,
  formatParts: Array<'years' | 'months' | 'weeks' | 'days' | 'hours' | 'minutes' | 'seconds'> = ['years', 'months', 'days', 'hours']
) => {
  const intervalStart = startOfDay(start)
  const intervalEnd = startOfNextDay(end)
  const days = differenceInCalendarDays(intervalEnd, intervalStart)
  return formatDurationDays(days, intervalStart.getFullYear(), formatParts)
}

/**
 * Converts an interval to duration so that hours are rounded to full days
 */
const millisecondsInDay = 24 * 60 * 60 * 1000
export const intervalToRoundedDuration = (interval: Interval) => {
  const normalizeDays = (duration: ReturnType<typeof intervalToDuration>) => ({
    ...duration,
    days: duration.days || 0,
  })
  const duration = intervalToDuration(interval)
  if (duration.hours) {
    const durationInMilliseconds = +interval.end - +interval.start
    if (duration.hours >= 12) {
      // round up
      const secondsToFullDay = Math.round((millisecondsInDay - (durationInMilliseconds % millisecondsInDay)) / 1000)
      const roundedInterval = { start: interval.start, end: add(interval.end, { seconds: secondsToFullDay }).getTime() }
      return normalizeDays(intervalToDuration(roundedInterval))
    } else {
      // round down
      const secondsToFullDay = Math.floor((durationInMilliseconds % millisecondsInDay) / 1000)
      const roundedInterval = { start: interval.start, end: sub(interval.end, { seconds: secondsToFullDay }).getTime() }
      return normalizeDays(intervalToDuration(roundedInterval))
    }
  } else {
    return normalizeDays(intervalToDuration(interval))
  }
}

export const formatISODate = (date: Date) => {
  return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`
}

/**
 * English month/day range with year on both ends (e.g. "Apr 20, 2025 – Apr 28, 2025", or "Apr 20, 2025" for a single day).
 * Used for KPI explanation windows; calendar tooltips use a narrower format in `EventKPIIndicator`.
 */
export const formatShortMonthDayRange = (start: Date, end: Date): string => {
  const fmt = (d: Date) => format(d, 'MMM d, yyyy')
  if (start.getTime() === end.getTime()) return fmt(start)
  return `${fmt(start)} – ${fmt(end)}`
}

/** Parses `yyyy-MM-dd` strings, sorts ascending, then formats min..max with {@link formatShortMonthDayRange}. */
export const formatSortedIsoDateStringsRange = (isoDates: string[]): string => {
  const parsed = isoDates.map((d) => parse(d, 'yyyy-MM-dd', new Date())).sort((a, b) => a.getTime() - b.getTime())
  if (parsed.length === 0) return ''
  return formatShortMonthDayRange(parsed[0], parsed[parsed.length - 1])
}

export const getIntervalByGranularity = (granularity: GranularityValue, date: Date | number) => {
  const start = startOfDay(date)
  let end
  switch (granularity) {
    case GranularityValue.Day:
      end = endOfDay(start)
      break
    case GranularityValue.Week:
      end = endOfWeek(start)
      break
    case GranularityValue.Month:
      end = endOfMonth(start)
      break
  }

  return { start, end } as Interval
}

export const getUTCDate = (date: Date | number | string) => {
  if (date instanceof UTCDate) {
    return date
  } else {
    const dateObj = new Date(date)
    return new UTCDate(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate())
  }
}
